import React, { useEffect, useState } from 'react'
import { apiFetch } from '../../api.js'
import { normalizeLogoPath } from '../../utils/branding.js'

const LOCALE_OPTIONS = [
  { value: 'fr-FR', label: 'Francais (fr-FR)' },
  { value: 'en-US', label: 'English US (en-US)' },
  { value: 'en-GB', label: 'English UK (en-GB)' },
  { value: 'pt-BR', label: 'Portugues (pt-BR)' },
  { value: 'ar-SA', label: 'Arabe (ar-SA)' },
]

const TIMEZONE_OPTIONS = [
  'Africa/Ouagadougou',
  'Africa/Abidjan',
  'Africa/Lagos',
  'Africa/Dakar',
  'Europe/Paris',
  'America/New_York',
  'UTC',
]

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
]

export default function SettingsGeneral({ onFlash }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  const [currency, setCurrency] = useState('XOF')
  const [currencySymbol, setCurrencySymbol] = useState('FCFA')
  const [locale, setLocale] = useState('fr-FR')
  const [timezone, setTimezone] = useState('Africa/Ouagadougou')
  const [defaultTvaPct, setDefaultTvaPct] = useState(18)
  const [orderPrefix, setOrderPrefix] = useState('CMD')
  const [purchasePrefix, setPurchasePrefix] = useState('PO')
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY')
  const [companyName, setCompanyName] = useState('')
  const [logoPath, setLogoPath] = useState('')
  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    try {
      setLoading(true)
      const [appSettings, invoiceFormat] = await Promise.all([
        apiFetch('/api/settings'),
        apiFetch('/api/settings/invoice-format'),
      ])

      if (appSettings) {
        setCurrency(appSettings.currency || 'XOF')
        setCurrencySymbol(appSettings.currency_symbol || 'FCFA')
        setLocale(appSettings.locale || 'fr-FR')
        setTimezone(appSettings.timezone || 'Africa/Ouagadougou')
        setDefaultTvaPct(Number(appSettings.default_tva_pct) || 18)
        setOrderPrefix(appSettings.order_prefix || 'CMD')
        setPurchasePrefix(appSettings.purchase_prefix || 'PO')
        setDateFormat(appSettings.date_format || 'DD/MM/YYYY')
      }
      if (invoiceFormat) {
        setCompanyName(invoiceFormat.company_name || '')
        setLogoPath(normalizeLogoPath(invoiceFormat.logo_path))
      }
    } catch (err) {
      onFlash?.('Erreur chargement parametres: ' + (err?.message || 'unknown'))
    } finally {
      setLoading(false)
    }
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)

    try {
      const [_, invoiceUpdated] = await Promise.all([
        apiFetch('/api/settings', {
          method: 'PUT',
          body: {
            currency,
            currency_symbol: currencySymbol,
            locale,
            timezone,
            default_tva_pct: String(defaultTvaPct),
            order_prefix: orderPrefix,
            purchase_prefix: purchasePrefix,
            date_format: dateFormat,
          },
        }),
        apiFetch('/api/settings/invoice-format', {
          method: 'PUT',
          body: {
            company_name: companyName,
          },
        }),
      ])

      if (invoiceUpdated?.logo_path !== undefined) {
        setLogoPath(normalizeLogoPath(invoiceUpdated.logo_path))
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('fedis-branding-updated'))
      }

      onFlash?.('Parametres generaux enregistres')
    } catch (err) {
      onFlash?.('Erreur sauvegarde: ' + (err?.message || 'unknown'))
    } finally {
      setSaving(false)
    }
  }

  async function handleUploadLogo(file) {
    if (!file) return
    if (!String(file.type || '').startsWith('image/')) {
      onFlash?.('Fichier invalide: image requise')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      onFlash?.('Fichier trop lourd (max 2MB)')
      return
    }

    const formData = new FormData()
    formData.append('logo', file)

    try {
      setUploadingLogo(true)
      const resp = await apiFetch('/api/invoice-template/logo', {
        method: 'POST',
        body: formData,
      })
      const nextLogo = resp?.logo_path || resp?.template?.logo_path || ''
      setLogoPath(normalizeLogoPath(nextLogo))
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('fedis-branding-updated'))
      }
      onFlash?.('Logo mis a jour')
    } catch (err) {
      onFlash?.('Erreur upload logo: ' + (err?.message || 'unknown'))
    } finally {
      setUploadingLogo(false)
    }
  }

  const resolvedLogoSrc = logoPath
    ? (logoPath.startsWith('http://') || logoPath.startsWith('https://') ? logoPath : logoPath)
    : ''

  if (loading) {
    return (
      <section className="pilot-card p-8 text-center">
        <div className="text-slate-500">Chargement des parametres...</div>
      </section>
    )
  }

  const inputCls = 'pilot-input'

  return (
    <section className="pilot-card">
      <div className="pilot-card-header">
        <h2 className="text-lg font-semibold text-slate-900">Parametres generaux</h2>
        <p className="text-sm text-slate-500 mt-1">
          Identite entreprise, devise, localisation, TVA et prefixes de reference.
        </p>
        <div className="pilot-divider" />
      </div>

      <form className="pilot-card-body grid gap-6 md:grid-cols-2" onSubmit={handleSave}>
        <div className="md:col-span-2 rounded-xl border border-slate-200 p-4 bg-slate-50/40">
          <div className="text-sm font-semibold text-slate-900 mb-3">Identite entreprise</div>
          <div className="grid gap-4 md:grid-cols-[1fr_auto]">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-slate-700 mb-2">Nom entreprise (sidebar)</label>
              <input
                className={inputCls}
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Nom de votre entreprise"
                maxLength={120}
              />
            </div>

            <div className="flex items-start gap-3">
              <div className="h-14 w-14 rounded-xl bg-white border border-slate-200 overflow-hidden flex items-center justify-center">
                {resolvedLogoSrc ? (
                  <img src={resolvedLogoSrc} alt="Logo entreprise" className="h-full w-full object-contain" />
                ) : (
                  <span className="text-[10px] text-slate-500 text-center px-1">Aucun logo</span>
                )}
              </div>
              <label className="pilot-btn border border-slate-300 text-slate-700 cursor-pointer">
                {uploadingLogo ? 'Upload...' : 'Changer logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  className="hidden"
                  disabled={uploadingLogo}
                  onChange={e => handleUploadLogo(e.target.files?.[0])}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Devise (code ISO)</label>
          <input
            className={inputCls}
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            placeholder="XOF, EUR, USD..."
            maxLength={5}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Symbole devise</label>
          <input
            className={inputCls}
            value={currencySymbol}
            onChange={e => setCurrencySymbol(e.target.value)}
            placeholder="FCFA, EUR, $..."
            maxLength={10}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Locale</label>
          <select className={inputCls} value={locale} onChange={e => setLocale(e.target.value)}>
            {LOCALE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Fuseau horaire</label>
          <select className={inputCls} value={timezone} onChange={e => setTimezone(e.target.value)}>
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Taux TVA par defaut</label>
          <div className="relative">
            <input
              type="number"
              className={inputCls}
              value={defaultTvaPct}
              onChange={e => setDefaultTvaPct(Number(e.target.value))}
              min={0}
              max={100}
              step={0.5}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Format de date</label>
          <select className={inputCls} value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
            {DATE_FORMAT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Prefixe commandes</label>
          <input
            className={inputCls}
            value={orderPrefix}
            onChange={e => setOrderPrefix(e.target.value)}
            placeholder="CMD"
            maxLength={10}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-slate-700 mb-2">Prefixe achats</label>
          <input
            className={inputCls}
            value={purchasePrefix}
            onChange={e => setPurchasePrefix(e.target.value)}
            placeholder="PO"
            maxLength={10}
          />
        </div>

        <div className="md:col-span-2 flex items-center justify-between pt-4 border-t border-slate-200">
          <div>
            <a
              href="/api/settings/gdpr/export"
              download
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export RGPD
            </a>
            <p className="text-xs text-slate-400 mt-0.5">Exporter toutes les données du compte (JSON)</p>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="pilot-btn pilot-btn-primary disabled:opacity-60"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </section>
  )
}
